import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import TsGrid from "../../components/tsGrid";
import { getTeamConfigDefs } from "./TeamConfigDefs";
import { toast } from "react-toastify";
import { MyContext } from "../../context/myContext";
import { Button } from "antd";
import CreateToolName from "./CreateToolName";
import { PlusOutlined } from "@ant-design/icons";
import { generateRandomId } from "../config/UserConfig";


interface ITeamConfigTable {
    afterUpdate: () => void
    getOrganization: () => any
    selectedTeam: any
    teamMemberList: any[]
    saveTeamMember: (data: any) => Promise<void>
}

const TeamConfigTable = (props: ITeamConfigTable) => {
    const { afterUpdate, getOrganization, selectedTeam, teamMemberList, saveTeamMember } = props;
    const gridRef = useRef<any>(null);
    const gridApiRef = useRef<any>(null);
    const containerStyle = useMemo(() => ({ width: '100%', height: '100%', marginBottom: '10px' }), []);
    const gridStyle = useMemo(() => ({ height: '90%', width: '100%', padding: "0px !important" }), []);
    const [rowData, setRowData] = useState<any[]>([])
    const { selectedProduct } = useContext<any>(MyContext);
    const [isNewToolIdModalOpen, setNewToolIdModalOpen] = useState<boolean>(false);
    const [uniqueToolKeys, setUniqueToolKeys] = useState<string[]>([])

    useEffect(() => {
        const newItem = { id: generateRandomId(), domain: `@${getOrganization()}`, team_id: selectedTeam?.id, team_name: selectedTeam?.name, product_name: selectedProduct };
        if (teamMemberList?.length) {
            setRowData([...teamMemberList, newItem])
        } else {
            setRowData([newItem])
        }
        const uniqueToolKeys = getAllUniqueToolKeys(teamMemberList);
        setUniqueToolKeys(uniqueToolKeys)
    }, [teamMemberList])

    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellClass: 'border-right',
            filter: true,
            suppressCellSelection: true,
            flex: 1
        };
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        params.api.sizeColumnsToFit();
    }, []);

    const scrollToBottom = useCallback(() => {
        if (gridApiRef.current) {
            const rowCount = gridApiRef.current.getDisplayedRowCount();
            gridApiRef.current.ensureIndexVisible(rowCount - 1, "bottom");
        }
    }, []);

    const addNewRow = useCallback(() => {
        const newItem = { id: generateRandomId(), domain: `@${getOrganization()}`, team_id: selectedTeam?.id, team_name: selectedTeam?.name, product_name: selectedProduct };
        if (gridApiRef.current) {
            gridApiRef.current.applyTransaction({ add: [newItem] });
            setTimeout(() => {
                scrollToBottom();
            }, 10);
        }
    }, [rowData, scrollToBottom]);

  

    const getAllRowsData = (): any[] => {
        const allRows: any[] = [];
        gridApiRef.current.forEachNode((node: any) => {
            if (node.data) {
                allRows.push(node.data);
            }
        });
        return allRows;
    };

    const getAllUniqueToolKeys = (data: any) => {
        const allToolKeys = new Set<string>();
        data.forEach((item: any) => {
            if (item.tool_ids) {
                Object.keys(item.tool_ids).forEach(key => allToolKeys.add(key));
            }
        });
        return Array.from(allToolKeys);
    }

    const onHandleCellValueChanged = useCallback((params: any) => {
        const rowCount = gridApiRef.current.getDisplayedRowCount();
        const rowNode = gridApiRef.current.getRowNode(params.node.rowIndex);
        const lastRowIndex = rowCount - 1;
        const data = rowNode.data;

        if (params?.colDef?.field === 'team_member_email') {
            data['team_member_email'] = params?.data?.team_member_email;

            const isAlreadyPresent = getAllRowsData()?.find((item: any) => (item?.team_member_email?.toLowerCase() === params?.data?.team_member_email?.toLowerCase()) && (typeof (item?.id) === 'number'));
            if (isAlreadyPresent) {
                data['team_member_email'] = '';
                gridApiRef.current.applyTransaction({ update: [data] });
                toast.error('Team Member already present', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
                return
            }

            if (params.rowIndex === lastRowIndex && data['team_member_email']) {
                addNewRow();
                gridApiRef.current.ensureIndexVisible(rowCount - 1, "bottom");
            }
        }
        gridApiRef.current.applyTransaction({ update: [data] });


        uniqueToolKeys.forEach(key => {
            if (Object.hasOwn(data, key)) {
                if (!data?.tool_ids) data["tool_ids"] = {}
                data['tool_ids'][key] = data[key];
            }
        });
        if (data?.team_member_email) {
            saveTeamMember(data);
        }

    }, [teamMemberList,uniqueToolKeys])

    function formatHeaderName(inputStr: string) {
        return inputStr?.replace(/_/g, ' ')
            .replace(/\w\S*/g, txt =>
                txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
            ) || inputStr;
    }

    const getDynamicColumns = useCallback(() => {

        const toolColumns = uniqueToolKeys.map(toolKey => ({
            field: `${toolKey}`,
            headerName: `${formatHeaderName(toolKey)} ID`,
            width: 120,
            tooltipField: `${toolKey}`,
            cellStyle: { "cursor": "pointer" },
            cellClass: 'border-right',
            show: true,
            filter: 'agMultiColumnFilter',
            menuTabs: ['filterMenuTab', 'generalMenuTab'],
            editable: true,
        }))

        return toolColumns
    }, [uniqueToolKeys])

    const columnDefs = useMemo(() => getTeamConfigDefs({
        afterUpdate,
        getOrganization,
        getDynamicColumns
    }), [rowData, afterUpdate, getOrganization,getDynamicColumns]);

    const addNewToolColumn = useCallback((newCol:string) => {
        setUniqueToolKeys((prevCols) => [...prevCols, newCol]);
        closeNewToolIdModal()
      }, []);

    const handleNewToolIdBtn = () => {
        setNewToolIdModalOpen(true);
    }

    const closeNewToolIdModal = () => {
        setNewToolIdModalOpen(false);
    }
    return (
        <div>
            <h1 className="page-heading">{selectedTeam?.name}</h1>

            <div style={containerStyle}>
                <div className="absolute top-9 right-3  ml-5">
                    <Button
                        size="middle"
                        icon={<PlusOutlined />}
                        onClick={handleNewToolIdBtn}
                    >
                        New Tool
                    </Button>
                </div>
                <div style={{ height: '85vh' }} className={`ag-theme-alpine grid_style mt-[30px] pr-[10px]`}>
                    <TsGrid
                        rowData={rowData}
                        columnDefs={columnDefs}
                        onGridReady={onGridReady}
                        gridRef={gridRef}
                        gridStyle={gridStyle}
                        defaultColDef={defaultColDef}
                        editType={"fullRowInline"}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                        virtualization={true}
                        onCellValueChanged={onHandleCellValueChanged}
                    />
                </div>
            </div>
            {isNewToolIdModalOpen
                && <CreateToolName
                    isNewToolIdModalOpen={isNewToolIdModalOpen}
                    closeModal={closeNewToolIdModal}
                    addNewToolColumn={addNewToolColumn}
                    uniqueToolKeys={uniqueToolKeys}
                />}
        </div>
    )
}

export default TeamConfigTable;